import React, { useCallback, useEffect, useState } from 'react'
import throttle from 'lodash.throttle'
import { matchPath, NavLink, useLocation } from 'react-router-dom'
import {
  LoadPageSeparator,
  Pluralize,
  Skeleton,
  TimeAgo,
  useForDesktop,
} from '@sketch/components'
import { selectors } from '@sketch/constants'
import { routes } from '@sketch/modules-common'

import {
  INBOX_SCROLL_CONTAINER_ATTR,
  ListHeader,
  ListWrapper,
  NewInboxBadgeArrowIcon,
  NewInboxNotificationsBadge,
} from './Inbox.styles'
import {
  InboxListItem,
  InboxListItemProps,
} from '../../components/InboxListItem'
import { NotificationTypeIcon } from '../../components/NotificationTypeIcon'
import { getNotificationConfig } from '../../components/InboxPreview/notificationConfig'
import { InboxItem, useInboxNotifications } from './useInboxNotifications'

const skeletonItem: InboxListItemProps = {
  state: 'read',
  avatar: <Skeleton width="100%" height="100%" />,
  title: <Skeleton width="80%" height="14px" />,
  subtitle: <Skeleton width="50%" height="13px" />,
}

const SKELETON_COUNT = 3
const LOAD_MORE_SKELETON_COUNT = 3
const SCROLLED_THRESHOLD_PX = 50
const SCROLL_THROTTLE_MS = 100

const PlaceholderAvatar = ({
  background = '#d1d1d1',
}: {
  background?: string
}) => (
  <div
    style={{
      width: 40,
      height: 40,
      borderRadius: 8,
      background,
    }}
  />
)

const toListItemProps = (item: InboxItem): InboxListItemProps => ({
  state: item.state,
  avatar: <PlaceholderAvatar />,
  typeIcon: (
    <NotificationTypeIcon
      icon={getNotificationConfig(item.typename).typeIcon}
      size="sm"
    />
  ),
  title: item.title,
  subtitle: item.subtitle,
  timeago: <TimeAgo short date={item.createdAt} />,
})

export const InboxList = () => {
  const location = useLocation()
  const isDesktop = useForDesktop()
  const {
    loading,
    error,
    groups,
    loadMore,
    after,
    newNotificationsCount,
    resetNewNotificationsCount,
  } = useInboxNotifications()

  // Track which element actually scrolls — the inner panel on desktop, the
  // page-level container on mobile — so the badge only surfaces when the
  // user has scrolled away from the top.
  const [isScrolled, setIsScrolled] = useState(false)

  const getScrollContainer = useCallback(() => {
    const selector = isDesktop
      ? `[${INBOX_SCROLL_CONTAINER_ATTR}]`
      : selectors.contentScrollable

    return document.querySelector(selector) as HTMLElement | null
  }, [isDesktop])

  useEffect(() => {
    const container = getScrollContainer()
    if (!container) return

    const handleScroll = throttle(() => {
      setIsScrolled(container.scrollTop > SCROLLED_THRESHOLD_PX)
    }, SCROLL_THROTTLE_MS)

    handleScroll()
    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      handleScroll.cancel()
      container.removeEventListener('scroll', handleScroll)
    }
  }, [getScrollContainer])

  // Notifications that arrive while the user is at the top are already
  // visible in place, so drop any pending count rather than surfacing a
  // stale "X new notifications" the next time they scroll.
  useEffect(() => {
    if (!isScrolled && newNotificationsCount > 0) {
      resetNewNotificationsCount()
    }
  }, [isScrolled, newNotificationsCount, resetNewNotificationsCount])

  const showBadge = isScrolled && newNotificationsCount > 0

  const handleBadgeClick = () => {
    getScrollContainer()?.scrollTo({ top: 0, behavior: 'smooth' })
    resetNewNotificationsCount()
  }

  const itemMatch = matchPath<{ notificationId: string }>(location.pathname, {
    path: routes.UPDATES_ITEM.template(),
    exact: true,
  })
  const activeNotificationId = itemMatch?.params?.notificationId || null

  if (loading) {
    return (
      <ListWrapper>
        <ListHeader>Loading…</ListHeader>
        {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
          <InboxListItem key={index} {...skeletonItem} />
        ))}
      </ListWrapper>
    )
  }

  if (error) {
    return (
      <ListWrapper>
        <ListHeader>Couldn’t load notifications</ListHeader>
      </ListWrapper>
    )
  }

  if (groups.length === 0) {
    return (
      <ListWrapper>
        <ListHeader>No notifications</ListHeader>
      </ListWrapper>
    )
  }

  return (
    <>
      <ListWrapper>
        {groups.map(group => (
          <React.Fragment key={group.id}>
            <ListHeader>{group.label}</ListHeader>
            {group.items.map(item => (
              <NavLink
                key={item.id}
                to={
                  item.id === activeNotificationId
                    ? routes.UPDATES.create({})
                    : routes.UPDATES_ITEM.create({ notificationId: item.id })
                }
              >
                <InboxListItem
                  {...toListItemProps(item)}
                  active={item.id === activeNotificationId}
                />
              </NavLink>
            ))}
          </React.Fragment>
        ))}

        {after && (
          <>
            <LoadPageSeparator key={after} loadNewPage={loadMore} />
            {Array.from({ length: LOAD_MORE_SKELETON_COUNT }).map(
              (_, index) => (
                <InboxListItem key={`load-more-${index}`} {...skeletonItem} />
              )
            )}
          </>
        )}
      </ListWrapper>

      {showBadge && (
        <NewInboxNotificationsBadge
          type="button"
          onClick={handleBadgeClick}
          data-testid="new-inbox-notifications-badge"
        >
          <NewInboxBadgeArrowIcon aria-hidden />
          <Pluralize
            singular="new notification"
            count={newNotificationsCount}
            showCount
            wrapperElement="span"
          />
        </NewInboxNotificationsBadge>
      )}
    </>
  )
}
